import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Image from "@tiptap/extension-image";
import { useEffect, useRef } from "react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import {
  Bold, Italic, Heading1, Heading2, Heading3, List, ListOrdered,
  Quote, Undo2, Redo2, Link as LinkIcon, Minus, Code, Strikethrough, ImagePlus,
} from "lucide-react";

const btn = (active) =>
  `w-9 h-9 grid place-items-center rounded-lg text-sm transition-colors ${
    active ? "bg-bizma-terracotta text-white" : "text-bizma-ink hover:bg-bizma-muted"
  }`;

async function uploadFile(file) {
  const fd = new FormData();
  fd.append("file", file, file.name || "upload.jpg");
  const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return fileUrl(data.path);
}

export default function RichTextEditor({ value, onChange, placeholder = "Tulis artikel di sini..." }) {
  const fileInputRef = useRef();

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { class: "text-bizma-terracotta underline" } }),
      Placeholder.configure({ placeholder }),
      Image.configure({ HTMLAttributes: { class: "rounded-2xl my-4" }, allowBase64: false }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: {
        class: "prose prose-neutral max-w-none min-h-[400px] focus:outline-none px-4 py-4",
        "data-testid": "blog-content-editor",
      },
      handlePaste: (view, event) => {
        const items = event.clipboardData?.items || [];
        for (const item of items) {
          if (item.type && item.type.startsWith("image/")) {
            const file = item.getAsFile();
            if (file) { event.preventDefault(); insertImageFile(file); return true; }
          }
        }
        return false;
      },
      handleDrop: (view, event) => {
        const files = event.dataTransfer?.files;
        if (files && files.length && files[0].type.startsWith("image/")) {
          event.preventDefault(); insertImageFile(files[0]); return true;
        }
        return false;
      },
    },
  });

  const insertImageFile = async (file) => {
    if (!editor) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Gambar maksimal 8MB"); return; }
    const toastId = toast.loading("Mengunggah gambar...");
    try {
      const url = await uploadFile(file);
      editor.chain().focus().setImage({ src: url }).run();
      toast.success("Gambar ditambahkan", { id: toastId });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal unggah gambar", { id: toastId });
    }
  };

  useEffect(() => {
    if (editor && value !== undefined && value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return <div className="input-bizma h-64 grid place-items-center text-bizma-subtext">Memuat editor...</div>;

  const setLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("URL:", prev);
    if (url === null) return;
    if (url === "") { editor.chain().focus().extendMarkRange("link").unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const onPickImage = () => fileInputRef.current?.click();
  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) insertImageFile(f);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className="rounded-2xl border border-bizma-border bg-white" data-testid="rte-wrap">
      <div className="flex flex-wrap items-center gap-1 px-2 py-2 border-b border-bizma-border bg-bizma-cream/50 rounded-t-2xl">
        <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} className={btn(editor.isActive("bold"))} title="Tebal" data-testid="rte-bold"><Bold size={16}/></button>
        <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} className={btn(editor.isActive("italic"))} title="Miring" data-testid="rte-italic"><Italic size={16}/></button>
        <button type="button" onClick={() => editor.chain().focus().toggleStrike().run()} className={btn(editor.isActive("strike"))} title="Coret" data-testid="rte-strike"><Strikethrough size={16}/></button>
        <span className="w-px h-6 bg-bizma-border mx-1"/>
        <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} className={btn(editor.isActive("heading", { level: 1 }))} title="Judul 1" data-testid="rte-h1"><Heading1 size={16}/></button>
        <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className={btn(editor.isActive("heading", { level: 2 }))} title="Judul 2" data-testid="rte-h2"><Heading2 size={16}/></button>
        <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} className={btn(editor.isActive("heading", { level: 3 }))} title="Judul 3" data-testid="rte-h3"><Heading3 size={16}/></button>
        <span className="w-px h-6 bg-bizma-border mx-1"/>
        <button type="button" onClick={() => editor.chain().focus().toggleBulletList().run()} className={btn(editor.isActive("bulletList"))} title="Daftar" data-testid="rte-ul"><List size={16}/></button>
        <button type="button" onClick={() => editor.chain().focus().toggleOrderedList().run()} className={btn(editor.isActive("orderedList"))} title="Daftar Berurutan" data-testid="rte-ol"><ListOrdered size={16}/></button>
        <button type="button" onClick={() => editor.chain().focus().toggleBlockquote().run()} className={btn(editor.isActive("blockquote"))} title="Kutipan" data-testid="rte-quote"><Quote size={16}/></button>
        <button type="button" onClick={() => editor.chain().focus().toggleCodeBlock().run()} className={btn(editor.isActive("codeBlock"))} title="Kode" data-testid="rte-code"><Code size={16}/></button>
        <button type="button" onClick={() => editor.chain().focus().setHorizontalRule().run()} className={btn(false)} title="Garis" data-testid="rte-hr"><Minus size={16}/></button>
        <span className="w-px h-6 bg-bizma-border mx-1"/>
        <button type="button" onClick={onPickImage} className={btn(false)} title="Sisipkan Gambar" data-testid="rte-image"><ImagePlus size={16}/></button>
        <button type="button" onClick={setLink} className={btn(editor.isActive("link"))} title="Tautan" data-testid="rte-link"><LinkIcon size={16}/></button>
        <span className="w-px h-6 bg-bizma-border mx-1"/>
        <button type="button" onClick={() => editor.chain().focus().undo().run()} className={btn(false)} title="Undo" data-testid="rte-undo"><Undo2 size={16}/></button>
        <button type="button" onClick={() => editor.chain().focus().redo().run()} className={btn(false)} title="Redo" data-testid="rte-redo"><Redo2 size={16}/></button>
        <input ref={fileInputRef} type="file" accept="image/*" onChange={onFileChange} className="hidden" data-testid="rte-image-input" />
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}

export function stripHtml(html) {
  if (!html) return "";
  try {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  } catch (e) { return String(html).replace(/<[^>]+>/g, ""); }
}
